import React, { useEffect, useRef, useState } from "react"
import LoadingOverlay from "react-loading-overlay"
import HeroImage from "../../assets/images/landing-page-header.jpg";
import HeroCircle from "../../assets/images/landing/hero_circle.png"
import Home4 from "../../assets/images/landing/home_1.svg"
import Home5 from "../../assets/images/landing/home_2.svg"
import Home6 from "../../assets/images/landing/home_3.svg"
import Home7 from "../../assets/images/landing/home_4.svg"
import AgraLogo from "../../assets/images/landing/agra_logo_svg.svg"
import RealTimeComponent from "./features/RealTime";
import BenchMarkAnalyseComponent from "./features/BenchMarkAnalyse";
import NewsUpdatesComponent from "./features/NewsUpdates";
import CustomizableDashComponent from "./features/CustomizableDash";
import { fetchLandingPageStats } from "services/landingPageService";

const divStyle = {
  backgroundImage: `url(${AgraLogo})`,
  backgroundSize: "cover",
  backgroundPosition: "center",
};

const Dashboard = () => {
  // Meta title
  document.title = "Dashboard | APRITS"

  LoadingOverlay.propTypes = undefined

  // State for typing
  const [text, setText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [loopNum, setLoopNum] = useState(0);
  const [typingSpeed, setTypingSpeed] = useState(150);

  // State for landing page stats
  const [landingPageStats, setLandingPageStats] = useState({
    agra_focus_countries: 0,
    total_policies: 0,
    total_systems: 0
  });
  
  // State for loading
  const [isLoading, setIsLoading] = useState(true);

  // Scroll refs
  const scrollFloater = useRef(null);
  const rotateDivRef = useRef(null);
  const scrollImageRef = useRef(null);

  const rotateDivImages = [
    Home4,
    Home5,
    Home6,
    Home7
  ]

  const getHostInfo = () => {
    const { protocol, hostname, port } = window.location;
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  const host_name = getHostInfo()

  // Fetch landing page stats using the service
  const getLandingPageStats = async () => {
    try {
      const response = await fetchLandingPageStats();
      setLandingPageStats(response);
    } catch (error) {
      console.error("Failed to load landing page stats:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const hookWindowScrollEvents = () => {
    const handleScroll = () => {
      // Calculate scroll progress for the features section
      const featuresSection = document.querySelector('section');
      if (!featuresSection) return;

      const sectionTop = featuresSection.offsetTop;
      const sectionHeight = featuresSection.offsetHeight;
      const windowScrollTop = window.scrollY;
      const windowHeight = window.innerHeight;

      // Calculate scroll progress within the features section
      const scrollStart = sectionTop;
      const scrollEnd = sectionTop + sectionHeight - windowHeight;
      
      // Normalize scroll progress (0 to 1)
      let progress = (windowScrollTop - scrollStart) / (scrollEnd - scrollStart);
      progress = Math.max(0, Math.min(1, progress)); // Clamp between 0 and 1

      // Rotate the circle based on scroll progress
      if (rotateDivRef.current) {
        rotateDivRef.current.style.transform = `rotate(${progress * 360}deg)`;
      }

      // Image switching logic
      const page = Math.floor(progress * 4);
      if (page >= 0 && page < rotateDivImages.length && scrollImageRef.current) {
        scrollImageRef.current.style.transition = 'opacity 0.1s ease-in-out';
        scrollImageRef.current.style.opacity = '0.5';
        
        setTimeout(() => {
          scrollImageRef.current.src = `${host_name}${rotateDivImages[page]}`;
          scrollImageRef.current.style.opacity = '1';
        }, 300);
      }

      // Scroll floater logic - preventing movement to the left
      if (scrollFloater.current) {
        if (windowScrollTop > window.innerHeight && windowScrollTop < window.height_to_stop) {
          scrollFloater.current.classList.add('fixed-left');
          scrollFloater.current.classList.remove('hidden');
        } else if (windowScrollTop >= window.height_to_stop) {
          // When scroll reaches the end, keep the floater visible but not fixed
          scrollFloater.current.classList.add('fixed-left');
          scrollFloater.current.classList.remove('hidden');
        } else {
          scrollFloater.current.classList.remove('fixed-left');
          scrollFloater.current.classList.remove('hidden');
        }
      }
    }
  
    // Add scroll event listener
    window.addEventListener('scroll', handleScroll);

    // Cleanup
    return () => {
      window.removeEventListener('scroll', handleScroll);
    }
  }

  // Add custom CSS for fixed left position
  useEffect(() => {
    // Create style element for fixed-left class
    const styleEl = document.createElement('style');
    styleEl.innerHTML = `
      .fixed-left {
        position: fixed;
        left: 0;
        top: 0;
        width: 50%;
        height: 100vh;
        z-index: 10;
      }
      
      @media (min-width: 768px) {
        .md\\:sticky {
          position: sticky;
          top: 0;
          display: block;
        }
      }
    `;
    document.head.appendChild(styleEl);
    
    window.height_to_stop = window.innerHeight * 4;
    const cleanup = hookWindowScrollEvents();
    
    // Fetch landing page stats when component mounts
    getLandingPageStats();
    
    return () => {
      cleanup();
      document.head.removeChild(styleEl);
    };
  }, []);

  const statements = [
    "Strengthening Governance, Accelerating Reforms",
    "Unveiling Policies' Progress, Shaping the Future",
    "Ultimately, Unlocking Africa's Agricultural Potential",
  ];

  useEffect(() => {
    const handleType = () => {
      const i = loopNum % statements.length;
      const fullText = statements[i];

      setText(
        isDeleting
          ? fullText.substring(0, text.length - 1)
          : fullText.substring(0, text.length + 1)
      );

      setTypingSpeed(isDeleting ? 30 : 150);

      if (!isDeleting && text === fullText) {
        setTimeout(() => setIsDeleting(true), 1000); // Pause before deleting
      } else if (isDeleting && text === "") {
        setIsDeleting(false);
        setLoopNum(loopNum + 1);
      }
    };

    const timer = setTimeout(handleType, typingSpeed);
    return () => clearTimeout(timer);
  }, [text, isDeleting, loopNum, typingSpeed, statements]);

  return (
    <React.Fragment>
      {/* Hero Section - Made responsive */}
      <div className="relative w-full p-3">
        {/* Hero Image Container with increased height to accommodate subheading */}
        <div className="relative w-full h-[60vh] sm:h-[70vh] md:h-[80vh] lg:h-[85vh] overflow-hidden">
          {/* Background Image */}
          <img 
            src={HeroImage}
            alt="Agricultural field with irrigation system"
            className="w-full h-full object-cover rounded-xl sm:rounded-2xl md:rounded-3xl"
          />
          {/* Overlay Content - Text size responsive with added space for subtitle */}
          <div className="absolute inset-0 flex flex-col justify-center px-4 sm:px-6 md:px-8">
            <h1 className="text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-2 sm:mb-4 max-w-xs sm:max-w-md md:max-w-2xl lg:max-w-3xl">
              Agri-Food Systems Policy Reform, and Implementation Tracking System (APRITS)
            </h1>
            <p className="text-white text-base sm:text-xl md:text-2xl italic mb-4 sm:mb-6 md:mb-8">
              {text}
              <span
                style={{
                  display: "inline-block",
                  width: "2px",
                  backgroundColor: "white",
                  marginLeft: "2px",
                  animation: "blink 1s infinite",
                }}
              >
                &nbsp;
              </span>
            </p>
          </div>
        </div>

        {/* Stats Section - Adjusted with space above for subheading */}
        <div className="absolute bottom-3 right-3 top-auto mt-16 sm:mt-20 md:mt-24 bg-white rounded-tl-xl sm:rounded-tl-2xl md:rounded-tl-3xl p-3 sm:p-4 md:p-6 lg:p-8 flex flex-row gap-4 sm:gap-8 md:gap-12 lg:gap-16 items-center">
          {isLoading ? (
            <div className="text-center">
              <p className="text-gray-700">Loading stats...</p>
            </div>
          ) : (
            <>
              <div className="text-center">
                <h2 className="text-[#2F7345] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-1 sm:mb-2 space-x-2 sm:space-x-3">
                 <span>🌍</span><span>15</span>
                </h2>
                <p className="text-gray-700 text-xs sm:text-sm md:text-base">AGRA-Focus Countries</p>
              </div>
              
              <div className="text-center">
                <h2 className="text-[#2F7345] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-1 sm:mb-2 space-x-2 sm:space-x-3">
                  <span>📊</span><span>{landingPageStats.total_policies}</span>
                </h2>
                <p className="text-gray-700 text-xs sm:text-sm md:text-base">Policies being Tracked</p>
              </div>
              
              <div className="text-center">
                <h2 className="text-[#2F7345] text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-semibold mb-1 sm:mb-2 space-x-2 sm:space-x-3">
                  <span>🌱</span><span>{landingPageStats.total_systems}</span>
                </h2>
                <p className="text-gray-700 text-xs sm:text-sm md:text-base">Agri-Food Systems Analyzed</p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Features Section - Fixed feature display on initial page */}
      {/* Desktop and Tablet View */}
      <div className="hidden md:block">
        <section className="mx-auto flex flex-col md:flex-row relative">
          <div 
            style={{...divStyle}} 
            id="scrollFloater" 
            ref={scrollFloater} 
            className="w-full md:w-1/2 h-screen md:left-0 md:top-0 md:bottom-0 md:sticky"
          > 
            <div className="relative">
              <h2 className="text-[#036F36] text-xl md:text-2xl lg:text-3xl absolute top-7 left-7">
                Features
              </h2>
            </div>
            <div className="flex h-full justify-center items-center">
              <div className="relative w-64 h-64 md:w-80 md:h-80 lg:w-[32rem] lg:h-[32rem]">
                <div className="w-full h-full absolute inset-0">
                  <img 
                    src={HeroCircle} 
                    id="rotateDiv" 
                    ref={rotateDivRef} 
                    className="w-full h-full transition-transform duration-100 ease-in-out" 
                    alt="Feature rotation circle"
                  />
                </div>
                <div className="flex items-center justify-center absolute top-0 left-0 bottom-0 right-0 w-full h-full">
                  <img 
                    ref={scrollImageRef} 
                    style={{width: "92%", height: "92%"}} 
                    className="rounded-lg object-contain p-6 md:p-8 lg:p-10 transition-opacity duration-300 ease-in-out" 
                    src={Home4} 
                    alt="Feature visual" 
                    id="scrollImage"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="w-full md:w-1/2">
            <div style={divStyle}>
              <RealTimeComponent />
            </div>
    
            <div style={divStyle}>
              <CustomizableDashComponent />
            </div>

            <div style={divStyle}>
              <BenchMarkAnalyseComponent />
            </div>

            <div style={divStyle}>
              <NewsUpdatesComponent />
            </div>
          </div>
        </section>
      </div>

      {/* Mobile View - Improved with better padding and spacing */}
      <section className="block md:hidden">
        {/* Features title for mobile view */}
        <h1 className="text-[#036F36] mt-8 mb-4 text-xl font-semibold text-center">
          Features
        </h1>
        <div className="w-11/12 mx-auto space-y-8">
          <div className="bg-white rounded-xl p-6 shadow-md" style={divStyle}>
            <RealTimeComponent />
          </div>
  
          <div className="bg-white rounded-xl p-6 shadow-md" style={divStyle}>
            <CustomizableDashComponent />
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md" style={divStyle}>
            <BenchMarkAnalyseComponent />
          </div>

          <div className="bg-white rounded-xl p-6 shadow-md" style={divStyle}>
            <NewsUpdatesComponent />
          </div>
        </div>
      </section>
    </React.Fragment>
  )
}

export default Dashboard